import { AUTOMATION_CHANNEL_OPTIONS } from './automationNotifyForm';
import {
  describeRuleConditions,
  feedPresetFromSource,
  normalizeAutomationRuleLogic,
} from './automationRuleLogic';
import { resolvedCategory } from './eventCategory';
import { logicSliceFromForm, type RuleFormValues } from './ruleFormModel';
import { type RuleStepStatus } from './RuleStep';

export type RuleStepId =
  | 'conditions'
  | 'event'
  | 'notify'
  | 'checklist'
  | 'assetChecklists';

export type RuleStepState = {
  status: RuleStepStatus;
  summary: string;
  /** Blocks save. Rendered as an error under the step and in the footer. */
  errors: string[];
  /** Worth fixing but saveable. */
  warnings: string[];
};

export function channelLabels(notificationTypes: string[]): string[] {
  return notificationTypes.flatMap((value) => {
    const label = AUTOMATION_CHANNEL_OPTIONS.find(
      (opt) => opt.value === value
    )?.label;
    return label ? [label] : [];
  });
}

/**
 * The two save blockers the legacy editor enforced in its submit handler. The
 * empty-URL case used to fail silently, so both are surfaced on step 1 now.
 */
export function conditionErrors(values: RuleFormValues): string[] {
  const normalized = normalizeAutomationRuleLogic(logicSliceFromForm(values));
  const errors: string[] = [];

  const emptyUrlCount = normalized.conditions.filter((condition) => {
    const preset = feedPresetFromSource(condition.source);
    return (
      (preset === 'custom' || preset === 'arcgis_layer') &&
      !(condition.source?.serviceUrl ?? '').trim()
    );
  }).length;
  if (emptyUrlCount > 0) {
    errors.push(
      emptyUrlCount === 1
        ? 'One condition has no layer selected. Pick a layer to save.'
        : `${emptyUrlCount} conditions have no layer selected. Pick a layer for each to save.`
    );
  }

  const emptyAorCount = normalized.conditions.filter(
    (condition) =>
      condition.kind === 'aor_intersect' &&
      (condition.aorIds?.length ?? 0) === 0
  ).length;
  if (emptyAorCount > 0) {
    errors.push(
      emptyAorCount === 1
        ? 'One AOR intersect condition has no AORs selected. Select at least one AOR to save.'
        : `${emptyAorCount} AOR intersect conditions have no AORs selected. Select at least one AOR in each to save.`
    );
  }

  return errors;
}

/**
 * AOR assignment stamps a region on the created event, so it can be set to a
 * strategy that this rule's conditions can never satisfy. Both cases are
 * warnings on the event step that read the condition step's state.
 */
export function eventWarnings(values: RuleFormValues): string[] {
  const conditions = values.conditions ?? [];
  const onlyAorIntersect =
    conditions.length > 0 &&
    conditions.every((condition) => condition.kind === 'aor_intersect');
  const hasAorIntersect = conditions.some(
    (condition) => condition.kind === 'aor_intersect'
  );

  if (values.aorStrategy === 'from_resources' && onlyAorIntersect) {
    return [
      'This rule only triggers on AOR overlap, so no resources may match and the region would stay empty. Choose From Impacted AOR(s) instead.',
    ];
  }
  if (values.aorStrategy === 'from_impacted_aors' && !hasAorIntersect) {
    return [
      'This rule has no AOR intersect condition, so nothing will be matched and the region would stay empty. Add an AOR intersect condition, or choose Fixed AOR(s).',
    ];
  }
  if (values.aorStrategy === 'fixed' && (values.aorIds?.length ?? 0) === 0) {
    return ['No AORs selected, so the created event will have no region.'];
  }
  return [];
}

export function conditionsStep(values: RuleFormValues): RuleStepState {
  const errors = conditionErrors(values);
  const count = values.conditions?.length ?? 0;
  const summary =
    count === 0
      ? 'No conditions yet'
      : describeRuleConditions(logicSliceFromForm(values));

  return {
    status:
      errors.length > 0 ? 'error' : count === 0 ? 'incomplete' : 'complete',
    summary,
    errors,
    warnings: [],
  };
}

export function eventStep(values: RuleFormValues): RuleStepState {
  const warnings = eventWarnings(values);
  const category = resolvedCategory(values.eventType, values.customCategory);
  const parts = [category, values.severity].filter(Boolean);

  return {
    status: warnings.length > 0 ? 'incomplete' : 'complete',
    summary: parts.length > 0 ? parts.join(' · ') : 'Not configured',
    errors: [],
    warnings,
  };
}

export function notifyRecipientCount(
  values: Pick<RuleFormValues, 'userIds' | 'teamIds' | 'roleScopeKeys'>
): number {
  return (
    (values.userIds?.length ?? 0) +
    (values.teamIds?.length ?? 0) +
    (values.roleScopeKeys?.length ?? 0)
  );
}

export function notifyStep(values: RuleFormValues): RuleStepState {
  const channels = channelLabels(values.notificationTypes ?? []);
  const recipients = notifyRecipientCount(values);
  const warnings: string[] = [];
  if (channels.length === 0) warnings.push('No channels selected.');
  if (recipients === 0) warnings.push('No recipients selected.');

  const summary =
    channels.length === 0 && recipients === 0
      ? 'Not configured'
      : `${channels.join(', ') || 'No channels'} · ${recipients} recipient${
          recipients === 1 ? '' : 's'
        }`;

  return {
    status: warnings.length > 0 ? 'incomplete' : 'complete',
    summary,
    errors: [],
    warnings,
  };
}

export function checklistStep(values: RuleFormValues): RuleStepState {
  const count = values.checklistItems?.length ?? 0;
  return {
    status: count === 0 ? 'incomplete' : 'complete',
    summary:
      count === 0 ? 'No tasks yet' : `${count} task${count === 1 ? '' : 's'}`,
    errors: [],
    warnings: count === 0 ? ['No tasks added yet.'] : [],
  };
}

export function assetChecklistsStep(assignedCount: number): RuleStepState {
  const count = Math.max(0, assignedCount);
  return {
    status: count === 0 ? 'incomplete' : 'complete',
    summary:
      count === 0 ? 'No assets yet' : `${count} asset${count === 1 ? '' : 's'}`,
    errors: [],
    warnings: count === 0 ? ['No assets assigned yet.'] : [],
  };
}

/** Every blocking message across the rule, for the footer and Save disabling. */
export function ruleErrors(values: RuleFormValues): string[] {
  const errors = [...conditionErrors(values)];
  if (!values.name?.trim()) errors.unshift('Name is required');
  return errors;
}
